/**
 * Frontend error funnel — batches uncaught errors and unhandled promise
 * rejections, then POSTs them to the unauthenticated /errors endpoint on
 * the HTTP API. From there the ErrorIngest lambda writes to the shared
 * ErrorsTable, which the local `vtt-errors` MCP server reads during
 * Cowork triage sessions.
 *
 * Design notes
 * ------------
 * - **Unauthenticated endpoint** so login-page crashes still report.
 * - **Best-effort, fire-and-forget**. Failures to POST are swallowed —
 *   we never want the error reporter itself to throw inside an error
 *   handler and trigger a feedback loop.
 * - **Batched** with a short flush window (2 s) so a burst of related
 *   errors lands as one HTTP call.
 * - **Capped** at 50 events per request (matches the Lambda's
 *   MAX_EVENTS_PER_REQUEST default) and 25 events buffered to keep
 *   memory bounded if the network is offline.
 * - **No PII**. Only the error message, stack, and a small static
 *   context block (release, route, viewport). Caller info / tokens
 *   never leave the browser through this path.
 */

export interface ErrorCaptureConfig {
  endpoint: string;
  release?: string;
  source?: string;
  flushIntervalMs?: number;
  maxBatch?: number;
  maxBuffer?: number;
}

interface ErrorEventPayload {
  source: string;
  severity: 'error' | 'warn' | 'info';
  message: string;
  stack?: string;
  release?: string;
  ts: number;
  context?: Record<string, unknown>;
}

let installed = false;
let buffer: ErrorEventPayload[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let cfg: Required<ErrorCaptureConfig> | null = null;

const DEFAULTS = {
  source: 'frontend',
  flushIntervalMs: 2000,
  maxBatch: 50,
  maxBuffer: 25,
  release: 'unknown',
};

function buildContext(): Record<string, unknown> {
  return {
    route: typeof window !== 'undefined' ? window.location.pathname : '',
    viewport:
      typeof window !== 'undefined'
        ? `${window.innerWidth}x${window.innerHeight}`
        : '',
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 200) : '',
  };
}

function enqueue(ev: ErrorEventPayload) {
  if (!cfg) return;
  if (buffer.length >= cfg.maxBuffer) {
    // Drop oldest — newer errors are usually more relevant.
    buffer.shift();
  }
  buffer.push(ev);
  scheduleFlush();
}

function scheduleFlush() {
  if (flushTimer || !cfg) return;
  flushTimer = setTimeout(flush, cfg.flushIntervalMs);
}

async function flush() {
  flushTimer = null;
  if (!cfg || buffer.length === 0) return;

  const batch = buffer.splice(0, cfg.maxBatch);
  try {
    // keepalive lets the request survive page unload, which is exactly
    // when boot-time crashes happen.
    await fetch(cfg.endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
      mode: 'cors',
      credentials: 'omit',
    });
  } catch {
    // Swallow. The caller's app keeps running.
  }
}

function reportError(
  err: unknown,
  severity: 'error' | 'warn' = 'error',
  extraContext?: Record<string, unknown>,
) {
  if (!cfg) return;
  const e = err as Error | undefined;
  enqueue({
    source: cfg.source,
    severity,
    message: (e?.message ?? String(err)).slice(0, 2000),
    stack: e?.stack?.slice(0, 8000),
    release: cfg.release,
    ts: Date.now(),
    context: { ...buildContext(), ...(extraContext ?? {}) },
  });
}

/**
 * Install global error + unhandledrejection listeners. Idempotent —
 * calling twice is a no-op so React StrictMode double-mounts are safe.
 */
export function installErrorCapture(config: ErrorCaptureConfig) {
  if (installed) return;
  if (typeof window === 'undefined') return;
  if (!config.endpoint) return;

  cfg = { ...DEFAULTS, ...config } as Required<ErrorCaptureConfig>;
  installed = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    reportError(event.error ?? new Error(event.message));
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    reportError(event.reason ?? new Error('Unhandled promise rejection'));
  });

  // Best-effort flush on tab close.
  window.addEventListener('pagehide', () => void flush());
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
}

/**
 * Manual reporter for caller-controlled error paths (try/catch blocks,
 * boundary fallbacks, etc.). Safe to call before installErrorCapture —
 * the call is just dropped.
 */
export function reportFrontendError(
  err: unknown,
  contextOrSeverity?: Record<string, unknown> | 'error' | 'warn',
  maybeSeverity: 'error' | 'warn' = 'error',
) {
  // Overloaded for ergonomics: callers can pass either a context bag or a
  // severity in the second slot. The most common case from page-level
  // catch blocks is `reportFrontendError(err, { source: 'page-x' })`.
  if (typeof contextOrSeverity === 'string') {
    reportError(err, contextOrSeverity);
  } else {
    reportError(err, maybeSeverity, contextOrSeverity);
  }
}
